
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText } from 'lucide-react';
import { Pagination } from '@/components/ui/pagination';

interface DocumentPageProps {
  content: string;
  index: number;
}

interface DocumentViewerProps {
  documentId: string;
  content: string[];
}

const DocumentPage: React.FC<DocumentPageProps> = ({ content, index }) => {
  return (
    <div className="bg-background border rounded-md p-6 mb-4">
      <h3 className="font-medium text-lg mb-3">Page {index + 1}</h3>
      <p className="whitespace-pre-line">{content}</p>
    </div>
  );
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({ documentId, content }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const totalPages = content.length;

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages - 1));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  return (
    <Card className="md:col-span-3">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Document Viewer
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="bg-muted/40 p-8 rounded-md mb-4 flex flex-col items-center justify-center">
          <FileText className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-medium">Document ID: {documentId}</h3>
          <p className="text-muted-foreground text-center mt-2">
            In a real implementation, this would display a PDF document using a library like react-pdf.
          </p>
        </div>

        {/* Only render the current page instead of all pages */}
        {content.length > 0 && (
          <DocumentPage key={currentPage} content={content[currentPage]} index={currentPage} />
        )}
      </CardContent>
      
      {totalPages > 1 && (
        <CardFooter className="flex justify-between">
          <Button
            variant="outline"
            onClick={handlePrevPage}
            disabled={currentPage === 0}
          >
            Previous
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">
              Page {currentPage + 1} of {totalPages}
            </span>
          </div>
          <Button
            variant="outline"
            onClick={handleNextPage}
            disabled={currentPage === totalPages - 1}
          >
            Next
          </Button>
        </CardFooter>
      )}
    </Card>
  );
};

export default DocumentViewer;
